import { inject, Service } from '@angular/core';
import { Actions, ofActionDispatched, ofActionSuccessful } from '@ngxs/store';
import { AddDataListPage, AddGridPage, AddItems, DeleteItems } from '@portal-core/data/collection/types/collection-state.type';
import { ModelIds } from '@portal-core/data/common/types/mc-model.type';
import { filter, map, merge, Observable } from 'rxjs';

export interface ActionItems<T> {
  new: T[];
  old: Dictionary<T>;
}

/**
 * CollectionActionsService
 * Provides observable actions for collection data stores.
 */
@Service()
export class CollectionActionsService {
  private readonly actions$ = inject(Actions);

  /**
   * Returns an observable that emits whenever items are permanently deleted from a data store — a
   * dispatched {@link DeleteItems} (i.e. `CollectionServiceBase.deleteItems$`). A `removeItems$` cache
   * eviction is deliberately not observed here, and there is no remove equivalent. Use this for cascade
   * side-effects, e.g. deleting a project's builds when the project itself is deleted. The emitted value
   * is the array of deleted item ids.
   * @param source the source name of the data store to observe
   */
  deleteItems$(source: string): Observable<ModelIds> {
    return this.itemsToDelete$(source);
  }

  /**
   * Returns an observable that emits whenever an item is added or updated in the data store.
   * The emitted value is an object with the new items as an array and the original version of those items as a dictionary.
   * @param source the source name of the data store to observe
   */
  updatedItems$<T>(source: string): Observable<ActionItems<T>> {
    return this.itemsFromAction$<T>(source);
  }

  /**
   * Observes the different actions that add/update items in the data store and filters them by a particular action.
   * @param source the source name of the data store to observe
   */
  private itemsFromAction$<T>(source: string): Observable<ActionItems<T>> {
    return merge(
      this.actions$.pipe(
        ofActionSuccessful(AddItems),
        filter((action: AddItems) => action.source === source),
        map((action: AddItems) => {
          return {
            new: action.payload.items ? Object.values(action.payload.items) : [],
            old: action.oldItems
          };
        })
      ),
      this.actions$.pipe(
        ofActionSuccessful(AddGridPage),
        filter((action: AddGridPage) => action.source === source),
        map((action: AddGridPage) => {
          return {
            new: action.payload.page.Items as unknown as T[],
            old: action.oldItems
          };
        })
      ),
      this.actions$.pipe(
        ofActionSuccessful(AddDataListPage),
        filter((action: AddDataListPage) => action.source === source),
        map((action: AddDataListPage) => {
          return {
            new: action.payload.page.Items as unknown as T[],
            old: action.oldItems
          };
        })
      )
    );
  }

  private itemsToDelete$(source: string): Observable<ModelIds> {
    return merge(
      this.actions$.pipe(
        ofActionDispatched(DeleteItems),
        filter((action: DeleteItems) => action.source === source),
        map((action: DeleteItems) => action.payload.itemIds)
      )
    )
  }
}
